import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, Phone } from "lucide-react";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);

  const handleGetConsultation = () => {
    navigate("/contact");
  };

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Products", path: "/products" },
    { name: "Services", path: "/services" },
    { name: "Why Us", path: "/whyus" },
    { name: "Resources", path: "/resources" },
    { name: "About Us", path: "/about" },
    { name: "Careers", path: "/careers" },
    { name: "Contact Us", path: "/contact" },
  ];

  const phoneNumber = "+91 93857 94811";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-zinc-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-20 flex items-center justify-between gap-4">
          
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0">
            <motion.div
              whileHover={{ scale: 1.03 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
            >
              <img
                src="/Assets/Images/icon.png"
                alt="TickBox Technologies"
                className="h-10 sm:h-11 w-auto object-contain"
              />
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;

              return (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Link
                    to={link.path}
                    className={`relative font-semibold text-sm xl:text-[15px] transition-all duration-300 ${
                      isActive
                        ? "text-[#d97925]"
                        : "text-zinc-800 hover:text-[#d97925]"
                    }`}
                  >
                    {link.name}

                    <span
                      className={`absolute -bottom-1 left-0 h-0.5 rounded-full transition-all duration-300 ${
                        isActive
                          ? "bg-[#d97925] w-full"
                          : "bg-[#d97925] w-0 group-hover:w-full"
                      }`}
                    />
                  </Link>
                </motion.div>
              );
            })}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3 shrink-0">
            
            {/* Phone */}
            <div className="hidden xl:flex items-center gap-2 text-zinc-700 font-medium text-sm whitespace-nowrap">
              <Phone size={18} className="text-[#d97925]" />
              <span>{phoneNumber}</span>
            </div>

            {/* Consultation Button */}
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              onClick={handleGetConsultation}
              className="hidden md:flex items-center gap-2 px-4 lg:px-5 py-2.5 bg-[#d97925] hover:bg-[#c06b20] text-white font-semibold text-sm rounded-xl shadow-md transition-all whitespace-nowrap"
            >
              <span className="hidden sm:block">Get Consultation</span>
              <span className="sm:hidden">Consult</span>

              <ArrowRight size={16} />
            </motion.button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-xl hover:bg-zinc-100 transition-colors"
            >
              {isOpen ? (
                <X size={24} className="text-zinc-800" />
              ) : (
                <Menu size={24} className="text-zinc-800" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden border-t border-zinc-200 bg-white/95 backdrop-blur-xl shadow-xl"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              
              {/* Mobile Links */}
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;

                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                      isActive
                        ? "bg-[#d97925]/10 text-[#d97925]"
                        : "text-zinc-800 hover:bg-zinc-100"
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}

              {/* Mobile Phone */}
              <div className="flex items-center gap-2 mt-4 px-4 text-sm text-zinc-700">
                <Phone size={18} className="text-[#d97925]" />
                <span>{phoneNumber}</span>
              </div>

              {/* Mobile CTA */}
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  setIsOpen(false);
                  navigate("/contact");
                }}
                className="mt-5 w-full py-3.5 bg-[#d97925] hover:bg-[#c06b20] text-white font-semibold rounded-2xl shadow-md transition-all"
              >
                Request Free Consultation
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;